import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import * as subMatFolderActions from '../../store/subMatFolder';
import SubMatFolderIcon from './SubMatFolderIcon';

const folderById = (folders) => new Map((folders || []).map((folder) => [folder.id, folder]));

const PickerNodes = ({
  parentId,
  depth,
  folders,
  sourceId,
  links,
  chosen,
  onChoose,
}) => {
  const nodes = subMatFolderActions.buildFolderTree(folders, parentId);
  if (!nodes.length) return null;
  return nodes.map((node) => {
    const isRoot = subMatFolderActions.isSystemRootFolder(node) || node.folderLevel === 0;
    const self = node.id === sourceId;
    const cycle = !isRoot && !self && subMatFolderActions.linkWouldCycle(links, sourceId, node.id);
    const blocked = isRoot || self || cycle;
    const already = chosen.some((id) => id === node.id);
    let reason = '';
    if (isRoot) reason = '최상위 폴더에는 붙일 수 없습니다.';
    else if (self) reason = '같은 폴더에는 붙일 수 없습니다.';
    else if (cycle) reason = '서로 물려 붙일 수 없습니다.';
    else if (already) reason = '이미 붙어 있습니다.';
    return (
      <Box key={node.id}>
        <ListItem
          disablePadding
          sx={{ pl: 1 + depth * 2, py: 0.25 }}
          secondaryAction={blocked || already ? null : (
            <Button size="small" onClick={() => onChoose(node.id)}>추가</Button>
          )}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, pr: 7 }}>
            <SubMatFolderIcon folder={node} />
            <ListItemText
              primary={node.name}
              secondary={reason || null}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </Box>
        </ListItem>
        <PickerNodes
          parentId={node.id}
          depth={depth + 1}
          folders={folders}
          sourceId={sourceId}
          links={links}
          chosen={chosen}
          onChoose={onChoose}
        />
      </Box>
    );
  });
};

const SubMatFolderLinkDialog = ({
  open,
  folder,
  folders,
  links,
  onClose,
  onError,
}) => {
  const dispatch = useDispatch();
  const [chosen, setChosen] = useState([]);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const byId = useMemo(() => folderById(folders), [folders]);

  useEffect(() => {
    if (!open || !folder) return;
    const current = (links || [])
      .filter((link) => link.sourceFolderId === folder.id)
      .map((link) => link.targetFolderId);
    setChosen(current);
    setPicking(false);
  }, [open, folder, links]);

  const save = async () => {
    if (!folder || saving) return;
    setSaving(true);
    try {
      await dispatch(subMatFolderActions.replaceFolderLinks(folder.id, chosen));
      onClose?.();
    } catch (err) {
      onError?.(err?.serverResponse?.message || err.message || '추가 서류를 붙이지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>{folder?.name ? `${folder.name} 추가 서류` : '추가 서류로 붙이기'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            고른 폴더가 자재승인서에 나오면 이 서류도 함께 들어갑니다.
          </Typography>
          {chosen.length === 0 ? (
            <Typography variant="body2" color="text.secondary">붙어 있는 폴더가 없습니다.</Typography>
          ) : (
            <List dense disablePadding>
              {chosen.map((id) => (
                <ListItem
                  key={id}
                  disablePadding
                  secondaryAction={(
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="빼기"
                      onClick={() => setChosen((prev) => prev.filter((item) => item !== id))}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  sx={{ pr: 5, py: 0.25 }}
                >
                  <ListItemText
                    primary={byId.get(id)?.name || '폴더'}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPicking(true)} disabled={saving}>폴더 추가</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>취소</Button>
          <Button variant="contained" onClick={save} disabled={saving}>저장</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={picking} onClose={() => setPicking(false)} fullWidth maxWidth="sm">
        <DialogTitle>붙일 폴더</DialogTitle>
        <DialogContent dividers sx={{ px: 1 }}>
          <PickerNodes
            parentId={null}
            depth={0}
            folders={folders}
            sourceId={folder?.id}
            links={links}
            chosen={chosen}
            onChoose={(id) => {
              setChosen((prev) => (prev.includes(id) ? prev : [...prev, id]));
              setPicking(false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPicking(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubMatFolderLinkDialog;
